import { Link, useNavigate, useParams } from 'react-router';
import { Icono } from '@xiltrion/ui/icono';
import { Skeleton } from '@xiltrion/ui/skeleton';
import { useSession } from '@/app/session';
import { DueBadge, ErrorState } from '@/components/ui';
import { useCase } from '@/features/cases/api';
import { CaseHeader } from '@/features/cases/CaseHeader';
import { useTask, type TaskDetail } from './api';
import { AssigneeHint, TaskSteps, TaskWork } from './TaskWorkPanel';

/** Task UI. Answers qué / por qué / cuándo / qué información / qué pasa después. */
export function TaskDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const q = useTask(id);
  if (q.isLoading) return <div className="flex flex-col gap-4" role="status" aria-label="Cargando tarea"><Skeleton className="h-16 w-full" /><Skeleton className="h-40 w-full" /><Skeleton className="h-64 w-full" /></div>;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} title="No pudimos cargar esta tarea." />;
  return <TaskView task={q.data} />;
}

function TaskView({ task }: { task: TaskDetail }) {
  const { me } = useSession();
  const nav = useNavigate();
  const mine = task.assigneeUserId === me?.userId;
  const caseDetail = useCase(task.caseId);
  const current = task.steps.find((s) => s.state === 'current')?.name ?? task.name;
  return (
    <div className="flex flex-col gap-4">
      <nav aria-label="Migas de pan" className="text-sm text-muted-foreground"><Link to="/tareas">Mis tareas</Link> <span aria-hidden>›</span> <Link to={`/pqrs/${task.caseId}`}>{task.case?.radication ?? 'Expediente'}</Link> <span aria-hidden>›</span> {task.name}</nav>
      {/* La cabecera del expediente, igual en todas sus tareas: lo esencial siempre a la vista. */}
      {caseDetail.data ? <CaseHeader c={caseDetail.data} linkToCase current={`Tarea: ${task.name}`} /> : <Skeleton className="h-36 w-full" />}
      {/* La tarea es su paso: los pasos a todo el ancho y, en una franja debajo, el paso actual, cuándo vence y a quién está asignada. */}
      <header className="bg-card border border-border rounded-lg shadow-xs">
        {task.steps.length > 1 && <div className="px-4 pt-5 pb-4"><TaskSteps steps={task.steps} open={['PENDING', 'IN_PROGRESS', 'BLOCKED'].includes(task.status)} blocked={task.status === 'BLOCKED'} /></div>}
        <div className={`px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm ${task.steps.length > 1 ? 'border-t border-border' : ''}`}>
          <span className="flex-1 min-w-0 truncate"><span className="text-muted-foreground">Paso actual:</span> <strong className="font-semibold">{current}</strong></span>
          <DueBadge due={task.due} dueAt={task.dueAt} />
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Icono nombre="mdi:account-outline" aria-hidden />
            <span className="sr-only">Asignada a</span>
            {task.assigneeUserId ? (mine ? <span className="text-foreground">Tú</span> : <><span className="text-foreground">{task.assigneeName ?? 'Otra persona'}</span> <AssigneeHint task={task} me={me?.userId} /></>) : 'Sin responsable'}
          </span>
        </div>
      </header>
      <TaskWork key={task.id} task={task} onDone={() => nav(`/pqrs/${task.caseId}`)} hideTaskMenu />
    </div>
  );
}
